import { useState } from "react";
import { Skeleton } from "antd";
import { Icon } from "@iconify/react";
import { Button } from "@/shared/components/button";
import { AppModal } from "@/shared/components/dialog";
import { toastError, toastSuccess } from "@/shared/lib/toast";
import {
  assignPatientPhysicianApi,
  getPatientPhysicianApi,
  removePatientPhysicianApi,
  type AssignedPhysician,
} from "@/portals/admin/api/adminPatientPhysicianApi";
import { adminQueryKeys, fetchAdminPhysicians } from "@/portals/admin/api/adminQueryKeys";
import { useCachedQuery } from "@/shared/hooks/useCachedQuery";
import type { PhysicianListItem } from "@/portals/physician/api/types";
import type { ContractError } from "@/portals/physician/api/contractErrors";
import { formatDate } from "@/features/admin/physicians/components/formatDate";
import { PhysicianPicker } from "@/features/admin/physicians/components/PhysicianPicker";
import { physicianName } from "@/portals/physician/lib/physicianName";

export const MESSAGES = {
  none: "No physician assigned. Items go to the shared queue.",
  removeConfirm: "Items this physician already holds will stay with them. New items will go to the shared queue.",
  patientNotFound: "Patient not found.",
  physicianNotFound: "That physician is no longer available.",
};

const BRAND = "!h-auto !rounded-lg !border-transparent !bg-[#E4002B]/50 !px-4 !py-2 !text-sm !font-semibold !text-white hover:!border-transparent hover:!bg-[#E4002B]/60 hover:!text-white disabled:!cursor-not-allowed disabled:!opacity-60";
const OUTLINE = "!h-auto !rounded-lg !border-[#E5E7EB] !px-4 !py-2 !text-sm !font-medium !text-[#4B5563] hover:!border-[#E4002B] hover:!text-[#E4002B]";

type Dialog = "assign" | "remove" | null;

export function AssignedPhysicianSection({ patientId }: { patientId: string }) {
  const [missingAfterAction, setMissingAfterAction] = useState(false);
  const [dialog, setDialog] = useState<Dialog>(null);
  const [choice, setChoice] = useState<string | undefined>(undefined);
  const [saving, setSaving] = useState(false);

  // Cached per patient: reopening this patient shows the assignment at once, then refreshes it quietly.
  const assignment = useCachedQuery(adminQueryKeys.patientPhysician(patientId), () => getPatientPhysicianApi(patientId), {
    onError: (error) => {
      if (error?.code !== "PATIENT_NOT_FOUND") toastError(error?.message ?? "Unable to load the assigned physician.");
    },
  });
  const physician: AssignedPhysician | null = assignment.data?.physician ?? null;
  const loading = assignment.loading;
  const patientMissing = missingAfterAction || (assignment.data === undefined && assignment.error?.code === "PATIENT_NOT_FOUND");
  const setPhysician = (next: AssignedPhysician | null) => assignment.mutate((prev) => ({ ...prev, physician: next }));

  // Only fetched while the assign dialog is open; shares the Physicians tab's cached list.
  const physicianList = useCachedQuery(dialog === "assign" ? adminQueryKeys.physicians : null, fetchAdminPhysicians, {
    onError: (error) => toastError(error?.message ?? "Unable to load physicians."),
  });
  const options: PhysicianListItem[] = physicianList.data ?? [];
  const loadOptions = physicianList.refresh;

  const openAssign = () => {
    setChoice(undefined);
    setDialog("assign");
  };

  // Toasts carry no names — only the fixed messages below.
  const handleError = (error: ContractError | null) => {
    if (error?.code === "PATIENT_NOT_FOUND") {
      toastError(MESSAGES.patientNotFound);
      setMissingAfterAction(true);
    } else if (error?.code === "PHYSICIAN_NOT_FOUND") {
      toastError(MESSAGES.physicianNotFound);
      void loadOptions();
    } else {
      toastError(error?.message ?? "Something went wrong.");
    }
  };

  const confirmAssign = async () => {
    if (!choice) return;
    setSaving(true);
    const { data, error } = await assignPatientPhysicianApi(patientId, choice);
    setSaving(false);
    if (data) {
      setPhysician(data.physician);
      setDialog(null);
      toastSuccess(physician ? "Assigned physician changed." : "Physician assigned.");
      return;
    }
    handleError(error);
    if (error?.code === "PATIENT_NOT_FOUND") setDialog(null);
  };

  const confirmRemove = async () => {
    setSaving(true);
    const { data, error } = await removePatientPhysicianApi(patientId);
    setSaving(false);
    setDialog(null);
    if (data) {
      setPhysician(null);
      toastSuccess("Assigned physician removed.");
      return;
    }
    handleError(error);
  };

  return (
    <section aria-label="Assigned physician" className="rounded-3xl border border-[#E5E7EB] bg-white p-5 shadow-sm">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-center gap-2.5">
          <div className="flex h-9 w-9 items-center justify-center rounded-full bg-[#E4002B]/50">
            <Icon icon="lucide:stethoscope" className="h-4 w-4 !text-white" />
          </div>
          <h3 className="text-sm font-extrabold text-[#0A2947]">Assigned physician</h3>
        </div>
        {!loading && !patientMissing && (
          <div className="flex gap-2">
            {physician && (
              <Button variant="outline" onClick={() => setDialog("remove")} className={OUTLINE}>
                Remove
              </Button>
            )}
            <Button onClick={openAssign} className={BRAND}>
              {physician ? "Change" : "Assign"}
            </Button>
          </div>
        )}
      </div>

      <div className="mt-4">
        {loading ? (
          <Skeleton active title={false} paragraph={{ rows: 2 }} />
        ) : patientMissing ? (
          <p className="text-sm text-[#9CA3AF]">{MESSAGES.patientNotFound}</p>
        ) : physician ? (
          <dl className="!m-0 grid gap-3 sm:grid-cols-3">
            {[
              { label: "Name", value: physicianName(physician) },
              { label: "Email", value: physician.email },
              { label: "Assigned", value: formatDate(physician.assignedAt) },
            ].map(({ label, value }) => (
              <div key={label} className="rounded-2xl border border-[#E5E7EB] bg-[#F9FAFB] p-3">
                <dt className="text-xs text-[#9CA3AF]">{label}</dt>
                <dd className="!m-0 mt-1 break-words text-sm font-semibold text-[#0A2947]">{value}</dd>
              </div>
            ))}
          </dl>
        ) : (
          <p className="text-sm text-[#6B7280]">{MESSAGES.none}</p>
        )}
      </div>

      <AppModal
        open={dialog === "assign"}
        onOpenChange={(open) => { if (!open && !saving) setDialog(null); }}
        title={<span className="text-[#0A2947]">{physician ? "Change assigned physician" : "Assign a physician"}</span>}
        description={<span className="text-[#4B5563]">New review items for this patient will go to the physician you choose.</span>}
        width={480}
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="outline" disabled={saving} onClick={() => setDialog(null)} className={OUTLINE}>Cancel</Button>
            <Button loading={saving} disabled={!choice} onClick={() => void confirmAssign()} className={BRAND}>
              {physician ? "Change physician" : "Assign physician"}
            </Button>
          </div>
        }
      >
        <PhysicianPicker physicians={options} value={choice} onChange={setChoice} excludeUserId={physician?.userId} />
      </AppModal>

      <AppModal
        open={dialog === "remove"}
        onOpenChange={(open) => { if (!open && !saving) setDialog(null); }}
        title={<span className="text-[#0A2947]">Remove assigned physician?</span>}
        description={<span className="text-[#4B5563]">{MESSAGES.removeConfirm}</span>}
        width={480}
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="outline" disabled={saving} onClick={() => setDialog(null)} className={OUTLINE}>Cancel</Button>
            <Button loading={saving} onClick={() => void confirmRemove()} className={BRAND}>Remove</Button>
          </div>
        }
      />
    </section>
  );
}
